<template>
  <aside class="documents-sidebar">
    <v-skeleton-loader v-if="isPending"
                       type="list-item, list-item, list-item"
                       class="documents-sidebar__loader"
    />
    <template v-if="isFolderTreeLoaded">
      <ul :class="{ 'mx-2': homeLayout && !mini }">
        <AppDroppableArea disabled
                          draggable="false"
                          @dragenter="dragEnterFolder(RECENT_FOLDER)"
                          @dragleave="dragLeaveFolder(RECENT_FOLDER)"
        >
          <DocumentsSidebarSectionItem icon="schedule"
                                       :section-name="recentFolderName"
                                       :selected="isSpecialFolderSelected(RECENT_FOLDER.id)"
                                       data-locator="recents-folder-item"
                                       @click="navigateToSpecialFolder(RECENT_FOLDER.id)"
          />
          <DocumentsSidebarSectionItem v-if="!homeLayout && !currentRoom.isDataroom && foldersTreeData.todoFolderId"
                                       icon="description"
                                       :section-name="$t('common.documents.todoFolder').toString()"
                                       :selected="isSpecialFolderSelected(foldersTreeData.todoFolderId)"
                                       data-locator="checklist-attachments-folder-item"
                                       @click="navigateToSpecialFolder(foldersTreeData.todoFolderId)"
          />

          <DocumentsSidebarSectionItem v-if="!homeLayout && currentRoom.isDataroom && foldersTreeData.qaFolderId"
                                       icon="attach_file"
                                       :section-name="$t('common.documents.qaFolder').toString()"
                                       :selected="isSpecialFolderSelected(foldersTreeData.qaFolderId)"
                                       data-locator="qa-attachments-folder-item"
                                       @click="navigateToSpecialFolder(foldersTreeData.qaFolderId)"
          />
        </AppDroppableArea>

        <li class="documents-sidebar__section-title">
          {{ $t('common.documents.folders') }}
        </li>

        <AppDroppableArea :disabled="!dropInCurrentFolderIsEnabled"
                          draggable="false"
                          @dragenter="dragEnterFolder(ROOT_FOLDER)"
                          @dragleave="dragLeaveFolder(ROOT_FOLDER)"
                          @ondrop="dropDocumentsInFolder($event, ROOT_FOLDER)"
        >
          <DocumentsSidebarSectionItem :icon="homeLayout ? 'home_storage' : 'folder'"
                                       :section-name="rootFolderName"
                                       :selected="isSpecialFolderSelected(ROOT_FOLDER.id)"
                                       data-locator="root-folder-item"
                                       @click="navigateToSpecialFolder(ROOT_FOLDER.id)"
          />
        </AppDroppableArea>

        <DocumentsSidebarFolderItem v-for="folder in sortedFolders"
                                    :key="folder.id"
                                    :folder="folder"
                                    :children-sort="foldersSortFn"
                                    :disabled="!dropInCurrentFolderIsEnabled"
                                    :draggable="true"
                                    @drag-enter-folder="dragEnterFolder"
                                    @drag-leave-folder="dragLeaveFolder"
                                    @drop-documents-in-folder="dropDocumentsInFolder"
                                    @dragstart.stop="dragClosdDocumentStart"
        />
      </ul>
    </template>
  </aside>
</template>

<script lang="ts">
import { last } from 'lodash-es'
import { defineComponent } from 'vue'
import { TranslateResult } from 'vue-i18n'
import { mapActions, mapGetters, mapState, mapMutations } from 'vuex'

import AppDroppableArea from '@/common/AppDroppableArea.vue'
import { DRAG_AND_DROP_MESSAGE_TYPE } from '@/common/utils/dragAndDropMessageType'
import { flatten } from '@/common/utils/flatArray'
import { sortNumbering } from '@/common/utils/sorting'
import { toast } from '@/common/utils/toast'
import { RECENT_FOLDER, ROOT_FOLDER } from '@/project/documents/constants/special-folders'
import { ROOMS_ROUTE_NAME } from '@/router/routerData'
import { AppUploadHandlerMutationTypes } from '@/store/modules/app-upload-handler/mutation_types'
import { GO_TO_FOLDER_PAGINATED, LOAD_FOLDERS_TREE, MOVE_FILDERS, DESELECT_ALL_DOCUMENTS } from '@/store/modules/documents/action_types'
import { SET_DOCUMENTS_TO_MOVE, SET_MOVEMENT_SNACKBAR_IS_OPEN, SET_NUMBERING_SNACKBAR_IS_OPEN } from '@/store/modules/documents/mutation_types'
import { DocumentsBreadcrumbActionTypes } from '@/store/modules/documents-breadcrumb/action_types'
import { DocumentsBreadcrumbMutationTypes } from '@/store/modules/documents-breadcrumb/mutation_types'
import { LOAD_PROJECTS_FOLDERS_TREE } from '@/store/modules/projects-folders/action_types'

import DocumentsSidebarFolderItem from './DocumentsSidebarFolderItem.vue'
import { type DocumentsSidebarFolder } from './DocumentsSidebarFolderItemContent.vue'
import DocumentsSidebarSectionItem from './DocumentsSidebarSectionItem.vue'

import type { SidebarSpecialFolder, SidebarDocumentId } from '../constants/types'

export type SidebarDocument = {
  id: SidebarDocumentId
  type: 'file' | 'folder'
  name?: string
  specialFolder?: string
} | SidebarSpecialFolder

type DragAndDropHintMessage = typeof DRAG_AND_DROP_MESSAGE_TYPE[keyof typeof DRAG_AND_DROP_MESSAGE_TYPE]

type DataType = {
  RECENT_FOLDER: SidebarSpecialFolder,
  ROOT_FOLDER: SidebarSpecialFolder,
  enteredDroppableFolders: Array<SidebarDocument>
}

export default defineComponent({
  name: 'DocumentsSidebar',
  components: {
    AppDroppableArea,
    DocumentsSidebarFolderItem,
    DocumentsSidebarSectionItem,
  },
  data (): DataType {
    return {
      ROOT_FOLDER: { ...ROOT_FOLDER, type: 'folder' },
      RECENT_FOLDER: { ...RECENT_FOLDER, type: 'folder' },
      enteredDroppableFolders: [],
    }
  },
  computed: {
    ...mapState('global', ['mini', 'homeLayout']),
    ...mapState('documents', ['foldersTree', 'getFoldersTreePending', 'documentsToMove', 'numberingEnabled', 'selectedDocuments', 'paginatedDocumentsCurrentFolderId']),
    ...mapState('documentsBreadcrumb', ['breadcrumb']),
    ...mapState('projectsFolders', ['projectsFoldersTree', 'getProjectsFoldersTreePending', 'currentProjectsFolder']),
    ...mapState('room', ['currentRoom']),
    ...mapGetters('room', ['currentUserRights', 'roomMnemo', 'isDataRoom']),
    ...mapGetters('documents', ['isSpecialFolderContext']),
    dropInCurrentFolderIsEnabled (): boolean {
      if (this.homeLayout || !this.documentsToMove.documents) {
        return false
      }
      if (this.isSpecialFolderContext) {
        return false
      }
      return this.userHasUploadRight && !(this.dropInSelectedFolder && this.dropSelectedDocuments) && !this.dropToSameFolder && !this.dropParentInChildFolder && !this.dropInSameParentFolder
    },
    dropToSameFolder (): boolean {
      return this.documentsToMove.documents?.some((document: SidebarDocument) =>
        document.id === this.focusedDroppableFolder?.id && document.type === 'folder',
      )
    },
    dropInSelectedFolder (): boolean {
      if (this.documentsToMove.isDragFromFoldersTree) {
        return false
      }
      return this.selectedDocuments.some((document: SidebarDocument) =>
        document.id === this.focusedDroppableFolder?.id && document.type === 'folder',
      )
    },
    dropSelectedDocuments (): boolean {
      return this.documentsToMove.isDragElementSelected
    },
    dropInSameParentFolder (): boolean {
      return this.documentsToMove.originalFolderId === (this.focusedDroppableFolder?.id || ROOT_FOLDER.id)
    },
    dropParentInChildFolder (): boolean {
      if (!this.documentsToMove.documents) {
        return false
      }
      return this.findMultiFoldersChildrenInTree().some(child => child.id === this.focusedDroppableFolder?.id)
    },
    focusedDroppableFolder (): SidebarDocument | undefined {
      return last(this.enteredDroppableFolders)
    },
    isFolderTreeLoaded (): boolean {
      return !this.isPending && !!this.foldersTreeData
    },
    sortedFolders (): Array<DocumentsSidebarFolder> {
      return [...this.foldersTree.folders].sort(this.foldersSortFn)
    },
    userHasUploadRight (): boolean {
      return this.currentUserRights.canUpload
    },
    recentFolderName (): TranslateResult | string {
      return this.homeLayout ? this.$t('projects.ProjectsHeader.recent') : this.$t('common.documents.recentFolder')
    },
    rootFolderName (): TranslateResult | string {
      return this.homeLayout ? this.$t('projects.ProjectsHeader.myProjects') : this.$t('common.documents.rootFolder')
    },
    foldersTreeData (): ObjectWith<{
      todoFolderId?: number | string
      qaFolderId?: number | string
    }> {
      return this.homeLayout ? this.projectsFoldersTree : this.foldersTree
    },
    isPending (): boolean {
      return this.homeLayout ? this.getProjectsFoldersTreePending : this.getFoldersTreePending
    },
  },
  watch: {
    focusedDroppableFolder (folder) {
      if (folder && this.documentsToMove.documents) {
        let messageType: DragAndDropHintMessage = DRAG_AND_DROP_MESSAGE_TYPE.MOVABLE
        if (folder.specialFolder || folder.id === RECENT_FOLDER.id) {
          messageType = DRAG_AND_DROP_MESSAGE_TYPE.ERROR_NOT_ALLOWED_FOLDER
        }
        if (this.dropInSelectedFolder && this.dropSelectedDocuments) {
          messageType = DRAG_AND_DROP_MESSAGE_TYPE.ERROR_DROP_IN_SELECTED_FOLDER
        }
        this.openUploadHint(folder.name, messageType)
        if (this.dropParentInChildFolder || this.dropInSameParentFolder || (this.dropToSameFolder && !(this.dropInSelectedFolder && this.dropSelectedDocuments))) {
          this.closeUploadHint()
        }
      } else {
        this.closeUploadHint()
      }
    },
    async '$route.query.displayedFolder' (folderID) {
      if (folderID) {
        await this.LOAD_FOLDERS_TREE(Number.parseInt(folderID as string))
      }
    },
  },
  async mounted () {
    if (this.$route.query['displayedFolder']) {
      await this.LOAD_FOLDERS_TREE(Number.parseInt(this.$route.query['displayedFolder'] as string))
    } else {
      await this.LOAD_FOLDERS_TREE()
    }
  },
  methods: {
    ...mapActions('projectsFolders', [LOAD_PROJECTS_FOLDERS_TREE]),
    ...mapActions('documents', [GO_TO_FOLDER_PAGINATED, LOAD_FOLDERS_TREE, MOVE_FILDERS, DESELECT_ALL_DOCUMENTS]),
    ...mapMutations('documents', [SET_DOCUMENTS_TO_MOVE, SET_MOVEMENT_SNACKBAR_IS_OPEN, SET_NUMBERING_SNACKBAR_IS_OPEN]),
    ...mapActions('documentsBreadcrumb', [DocumentsBreadcrumbActionTypes.SET_BREADCRUMB_ITEMS, DocumentsBreadcrumbActionTypes.ADD_BREADCRUMB_ITEM]),
    ...mapMutations('documentsBreadcrumb', [DocumentsBreadcrumbMutationTypes.UPDATE_FOLDER_NAVIGATION_TOKEN]),
    ...mapMutations('appUploadHandler', [AppUploadHandlerMutationTypes.SET_UPLOAD_HINT]),
    navigateToSpecialFolder (folderId: number | string) {
      if (this.homeLayout) {
        if (this.currentProjectsFolder.id && folderId === 0) {
          this.$router.push({ name: ROOMS_ROUTE_NAME })
        }
        return
      }

      this.UPDATE_FOLDER_NAVIGATION_TOKEN()

      if (folderId === ROOT_FOLDER.id) {
        this.SET_BREADCRUMB_ITEMS([{
          id: ROOT_FOLDER.id,
          name: this.$t('common.documents.rootFolder'),
        }])
      } else if (folderId === RECENT_FOLDER.id) {
        this.SET_BREADCRUMB_ITEMS([{
          id: RECENT_FOLDER.id,
          name: this.$t('common.documents.recentFolder'),
        }])
      } else {
        const isTodo = folderId === this.foldersTreeData.todoFolderId
        const isQa = folderId === this.foldersTreeData.qaFolderId

        if (isTodo) {
          this.SET_BREADCRUMB_ITEMS([{
            id: folderId,
            name: this.$t('common.documents.todoFolder'),
            specialFolder: 'todo',
          }])
        } else if (isQa) {
          this.SET_BREADCRUMB_ITEMS([{
            id: folderId,
            name: this.$t('common.documents.qaFolder'),
            specialFolder: 'qa',
          }])
        }
      }

      this.GO_TO_FOLDER_PAGINATED(folderId)
    },
    isSpecialFolderSelected (folderId: number | string): boolean {
      if (this.homeLayout) {
        return this.currentProjectsFolder.id === (folderId || undefined)
      }
      if (folderId === RECENT_FOLDER.id) {
        return this.paginatedDocumentsCurrentFolderId === RECENT_FOLDER.id
      }
      if (folderId === ROOT_FOLDER.id) {
        return this.paginatedDocumentsCurrentFolderId === ROOT_FOLDER.id
      }
      return this.breadcrumb[0]?.id === folderId
    },
    foldersSortFn (folderA: DocumentsSidebarFolder, folderB: DocumentsSidebarFolder) {
      if (folderA.numbering || folderB.numbering) {
        return sortNumbering(
          folderA.numbering ?? 9999999999,
          folderB.numbering ?? 9999999999,
        )
      } else {
        return folderA.name.localeCompare(folderB.name, undefined, {
          numeric: true,
          sensitivity: 'base',
        })
      }
    },
    dragEnterFolder (folder: SidebarDocument) {
      this.enteredDroppableFolders.push(folder)
    },
    dragLeaveFolder (folder: SidebarDocument) {
      this.enteredDroppableFolders = this.enteredDroppableFolders.filter(
        droppableFolderEntered => droppableFolderEntered.id !== folder.id,
      )
    },
    dropDocumentsInFolder ($event: DragEvent, childFolder: SidebarDocument) {
      const documentFromClosd = $event.dataTransfer?.getData('fromClosd') === 'true'
      if (documentFromClosd) {
        if (this.dropInCurrentFolderIsEnabled) {
          this.moveClosdDocument(childFolder)
        }
      }
    },
    dragClosdDocumentStart ($event: DragEvent, draggedFolder: SidebarDocument) {
      this.SET_MOVEMENT_SNACKBAR_IS_OPEN(false)
      const documentsToMoveArray = [{ id: draggedFolder.id, type: 'folder' }]
      const parentFolderId = this.findParentFolderId(draggedFolder)
      this.SET_DOCUMENTS_TO_MOVE({
        isDragElementSelected: this.selectedDocuments.some((document: SidebarDocument) => document.id === draggedFolder.id && document.type === 'folder'),
        isDragFromFoldersTree: true,
        documents: documentsToMoveArray,
        originalFolderId: parentFolderId || ROOT_FOLDER.id,
      })
      $event.dataTransfer?.setData('fromClosd', 'true')
      $event.dataTransfer?.setData('documentId', draggedFolder.id.toString())
      $event.dataTransfer?.setData('documentType', 'folder')
      $event.dataTransfer?.setData('documentParentId', parentFolderId || ROOT_FOLDER.id)
    },
    async moveClosdDocument (targetFolder: SidebarDocument) {
      try {
        await this.MOVE_FILDERS({
          documents: this.documentsToMove.documents,
          targetFolderId: targetFolder.id,
        })
        this.SET_MOVEMENT_SNACKBAR_IS_OPEN(true)
      } catch {
        toast.error(this.$t('project.documents.table.DocumentsTableBody.moveError'))
        return
      }

      if (this.numberingEnabled) {
        this.SET_NUMBERING_SNACKBAR_IS_OPEN(true)
      }

      if (this.documentsToMove.documents.some((document: SidebarDocument) => document.type === 'folder')) {
        await this.LOAD_FOLDERS_TREE()
      }
      if (this.documentsToMove.isDragElementSelected) {
        this.DESELECT_ALL_DOCUMENTS()
      }
    },
    openUploadHint (destinationFolder: string, messageType: DragAndDropHintMessage) {
      this.SET_UPLOAD_HINT({
        visible: true,
        destinationFolder: destinationFolder,
        messageType: messageType,
      })
    },
    closeUploadHint () {
      this.SET_UPLOAD_HINT({ visible: false })
    },
    findMultiFoldersChildrenInTree () {
      const foldersToMove = this.documentsToMove.documents.filter((document: SidebarDocument) => document.type === 'folder')
      const flattenFolders = flatten([], this.foldersTree?.folders)
      const childrenOfFoldersToMove: Array<SidebarDocument> = []
      foldersToMove.forEach((folderToMove: SidebarDocument) => {
        const matchingFolder = flattenFolders.find(flattenFolder => flattenFolder.id === folderToMove.id)
        if (matchingFolder && matchingFolder.children && matchingFolder.children.length > 0) {
          childrenOfFoldersToMove.push(...flatten([], matchingFolder.children))
        }
      })
      return childrenOfFoldersToMove
    },
    findParentFolderId (draggedFolder: SidebarDocument) {
      if (draggedFolder) {
        const flattenFolders = flatten([], this.foldersTree?.folders)
        for (const folder of flattenFolders) {
          if (folder.children && folder.children.some((child: SidebarDocument) => child.id === draggedFolder.id)) {
            return folder.id
          }
        }
        return null
      }
    },
  },
})
</script>

<style lang="scss" scoped>
.documents-sidebar {
  ul {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  &__section-title {
    margin-top: 16px;
    margin-bottom: 4px;
    padding: 0 12px;
    color: rgb(var(--v-theme-text-primary));
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
  }

  &__loader {
    :deep(.v-skeleton-loader__list-item) {
      background: transparent;
      height: 40px;
    }
  }
}
</style>
